import React, { useEffect, useState } from "react";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import StarIcon from "@mui/icons-material/Star";
import { useTranslation } from "react-i18next";
import { SERVER_URL } from "../api";
import { getLocalidadById } from "../api/localidad";
import { getRatingByBusiness } from "../api/business";
import logoSmall from "../assets/logo_small.png";
import LanguageSelector from "./LanguageSelector";
import "./BusinessBrandingHeader.css";

function resolveStoredMediaUrl(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("http") ? trimmed : `${SERVER_URL}/${trimmed}`;
}

function looksLikeObjectId(value) {
  return typeof value === "string" && /^[0-9a-fA-F]{24}$/.test(value.trim());
}

function extractLocalidadText(localidadRaw) {
  if (!localidadRaw) return "";
  if (typeof localidadRaw === "object") {
    return (
      localidadRaw.fullName ||
      localidadRaw.nombre ||
      localidadRaw.name ||
      ""
    );
  }
  if (looksLikeObjectId(localidadRaw)) return "";
  return String(localidadRaw);
}

/**
 * Cabecera compacta (logo + negocio) para pantallas de tip/pago y carta.
 * Misma lógica de logo que FormScreen (plan free → Tipify).
 * `showRating` y `showLanguageSelector` añaden la fila meta como en propinas.
 */
export default function BusinessBrandingHeader({
  business,
  showRating = false,
  showLanguageSelector = false,
}) {
  const { t } = useTranslation();
  const [localidadName, setLocalidadName] = useState(() =>
    extractLocalidadText(business?.localidad),
  );
  const [ratingValue, setRatingValue] = useState(null);

  useEffect(() => {
    if (!showRating || !business?._id) return undefined;
    let cancelled = false;
    getRatingByBusiness(business._id)
      .then((res) => {
        if (cancelled) return;
        if (res.status === 200 && res.data?.rating != null) {
          setRatingValue(Number(res.data.rating).toFixed(1));
        }
      })
      .catch(() => {
        /* sin rating: no se muestra */
      });
    return () => {
      cancelled = true;
    };
  }, [showRating, business?._id]);

  useEffect(() => {
    let cancelled = false;
    const raw = business?.localidad;

    const fromObject = extractLocalidadText(raw);
    if (fromObject) {
      setLocalidadName(fromObject);
      return undefined;
    }

    const id =
      typeof raw === "object" && raw?._id
        ? String(raw._id)
        : looksLikeObjectId(raw)
          ? String(raw).trim()
          : null;

    if (!id) {
      setLocalidadName("");
      return undefined;
    }

    getLocalidadById(id)
      .then((res) => {
        if (cancelled) return;
        const name = res?.data?.fullName || res?.data?.nombre || "";
        setLocalidadName(name);
      })
      .catch(() => {
        if (!cancelled) setLocalidadName("");
      });

    return () => {
      cancelled = true;
    };
  }, [business?.localidad]);

  if (!business) return null;

  const isOnFreePlan = business.planSuscripcionFree === true;
  const hasVives = business.vives === true;
  const suppressBusinessLogo = isOnFreePlan && !hasVives;
  const businessLogo = suppressBusinessLogo
    ? null
    : resolveStoredMediaUrl(business.logoURL);
  const logoSrc = businessLogo || logoSmall;
  const name = business.nombre_comercial || "";
  const addressParts = [
    business.direccion,
    business.codigo_postal,
    localidadName,
  ].filter(Boolean);
  const address = addressParts.join(", ");

  return (
    <div className="business-branding-header">
      <div className="business-branding-logo">
        <img
          src={logoSrc}
          alt={businessLogo ? name || "Logo" : "Tipify"}
        />
      </div>
      <div className="business-branding-meta">
        {name ? (
          <div className="business-branding-name">
            <FmdGoodOutlinedIcon
              className="me-1"
              sx={{ fontSize: 16, verticalAlign: "text-bottom" }}
            />
            <strong>{name}</strong>
          </div>
        ) : null}
        {address ? (
          <div className="business-branding-address">{address}</div>
        ) : null}
        {(showRating && ratingValue) || showLanguageSelector ? (
          <div className="business-branding-meta-row">
            {showRating && ratingValue ? (
              <div className="business-branding-rating">
                <span>{t("common.rating")}</span>
                <StarIcon sx={{ color: "#ffb304", fontSize: "15px" }} />
                <span className="business-branding-rating-value">
                  {ratingValue}
                </span>
              </div>
            ) : null}
            {showLanguageSelector ? <LanguageSelector /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function getBusinessBrandPrimary(business) {
  return business?.estilosMarca?.colorPrimario || "#66bc5f";
}
